/**
 * AuthContext - single source of truth for authentication state.
 *
 * Reactive: components consuming useAuth() re-render when auth changes
 * (login/logout). Hydrates from the existing localStorage token via the
 * existing authService on mount, refreshing the user from /auth/me.
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import api from '../services/api';
import authService from '../services/authService';

const AuthContext = createContext(null);

const ADMIN_ROLES = ['admin', 'super_admin'];

export const AuthProvider = ({ children }) => {
  // Initialise synchronously from localStorage so guards have a value on first render.
  const [token, setToken] = useState(() => api.getAuthToken());
  const [user, setUser] = useState(() => authService.getStoredUser());
  const [loading, setLoading] = useState(true);

  // On mount: if we have a token, refresh the user from the API. A failure
  // (expired/invalid token) clears auth.
  useEffect(() => {
    let active = true;
    const hydrate = async () => {
      if (!api.getAuthToken()) {
        setLoading(false);
        return;
      }
      try {
        const freshUser = await authService.getCurrentUser();
        if (active && freshUser) setUser(freshUser);
      } catch {
        if (active) {
          authService.logout();
          setToken(null);
          setUser(null);
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    hydrate();
    return () => {
      active = false;
    };
  }, []);

  // Keep context in sync if another part of the app updates the stored user
  // (e.g. profile picture upload dispatches 'profile-updated').
  useEffect(() => {
    const onProfileUpdated = () => setUser(authService.getStoredUser());
    window.addEventListener('profile-updated', onProfileUpdated);
    return () => window.removeEventListener('profile-updated', onProfileUpdated);
  }, []);

  const login = useCallback((nextUser, nextToken) => {
    if (nextToken) {
      api.setAuthToken(nextToken);
      setToken(nextToken);
    }
    if (nextUser) {
      api.setCurrentUser(nextUser);
      setUser(nextUser);
    }
  }, []);

  const logout = useCallback(() => {
    authService.logout();
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(() => {
    const isAuthenticated = !!token;
    const role = user?.role || null;
    return {
      user,
      token,
      role,
      isAuthenticated,
      isAdmin: isAuthenticated && ADMIN_ROLES.includes(role),
      loading,
      login,
      logout,
      setUser,
    };
  }, [user, token, loading, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};

export default AuthContext;
